import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { login } from "../lib/authApi";
import { useAuth } from "../auth/AuthContext";
import { ShieldCheck, Lock } from "@phosphor-icons/react";
import { ThemeToggle } from "../components/ThemeToggle";

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const { refresh } = useAuth();
  const expiredHint =
    loc.state?.from || loc.state?.forbidden
      ? t("ui.login.session_expired")
      : null;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const r = await login(email.trim(), password);
      await refresh();
      nav(r.must_change_password ? "/change-password" : "/");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || JSON.stringify(d)) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4 relative">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <form
        onSubmit={submit}
        className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-8 w-full max-w-[420px] space-y-5 rounded-2xl shadow-sm"
      >
        <div className="flex items-center gap-3 mb-1">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center shadow-sm">
            <ShieldCheck size={20} weight="bold" className="text-white" />
          </div>
          <div className="leading-tight">
            <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">GD VAT</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">{t("ui.login.title")}</div>
          </div>
        </div>
        {expiredHint && (
          <div
            data-testid="login-session-expired"
            className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2"
          >
            {expiredHint}
          </div>
        )}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">{t("ui.login.email_placeholder")}</label>
          <input
            data-testid="login-email"
            type="text"
            placeholder={t("ui.login.email_placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 text-[15px] rounded-lg text-slate-900 dark:text-slate-50 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900 dark:ring-teal-900 outline-none transition"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
            <Lock size={12} weight="bold" className="text-slate-500 dark:text-slate-400" />
            {t("ui.login.password_placeholder")}
          </label>
          <input
            data-testid="login-password"
            type="password"
            placeholder={t("ui.login.password_placeholder")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 text-[15px] rounded-lg text-slate-900 dark:text-slate-50 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900 dark:ring-teal-900 outline-none transition"
          />
        </div>
        {error && (
          <div data-testid="login-error" className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/40 border border-rose-200 dark:border-rose-800 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <button
          data-testid="login-submit"
          type="submit"
          disabled={busy}
          className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2.5 text-sm rounded-lg transition-colors shadow-sm"
        >
          {busy ? t("ui.login.busy") : t("ui.login.submit")}
        </button>
      </form>
    </div>
  );
}
